/**
 * Seeded pseudo-random number generator.
 *
 * Uses Mulberry32 — a small, fast 32-bit state generator with good statistical
 * properties for game-simulation use. NOT cryptographically secure.
 *
 * Simulation determinism requirement (MULTIPLAYER §2):
 *   Given the same seed and the same sequence of calls, this RNG must produce
 *   the same stream of values on any host. Mulberry32 is entirely integer
 *   arithmetic with Math.imul + >>> 0, so it behaves identically across any
 *   JavaScript engine.
 *
 * State is represented as a single 32-bit unsigned integer so it can be
 * serialised into save files as a plain number.
 */

export interface RngState {
  s: number; // uint32
}

/** Create a new RNG state from a seed. Seed is coerced to a 32-bit unsigned. */
export function createRng(seed: number): RngState {
  return { s: seed >>> 0 };
}

/**
 * Advance the RNG and return the next uint32. Mutates the passed state.
 * Pure function of the input state.
 */
export function nextU32(rng: RngState): number {
  rng.s = (rng.s + 0x6d2b79f5) >>> 0;
  let t = rng.s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (t ^ (t >>> 14)) >>> 0;
}

/** Next float in [0, 1). */
export function nextFloat(rng: RngState): number {
  return nextU32(rng) / 0x100000000;
}

/** Next integer in [min, maxExclusive). */
export function nextInt(rng: RngState, min: number, maxExclusive: number): number {
  if (!(maxExclusive > min)) {
    throw new Error(`nextInt: maxExclusive (${maxExclusive}) must be > min (${min})`);
  }
  const span = maxExclusive - min;
  return min + Math.floor(nextFloat(rng) * span);
}

/** Bernoulli trial: returns true with probability p (clamped to [0,1]). */
export function chance(rng: RngState, p: number): boolean {
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
  return nextFloat(rng) < clamped;
}

/** Uniform pick from an array. Throws on empty arrays. */
export function pick<T>(rng: RngState, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error('pick: array is empty');
  return arr[nextInt(rng, 0, arr.length)]!;
}

/** Snapshot the RNG state (structural copy so mutation of the RNG is isolated). */
export function snapshotRng(rng: RngState): RngState {
  return { s: rng.s >>> 0 };
}
